import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import useAuthStore from '../stores/authStore';
import useProviderProfileStore from '../stores/providerProfileStore';
import useStaffProfileStore from '../stores/staffProfileStore';
import usePermissions from '../hooks/usePermissions';
import { UserRole, USER_ROLE } from '../types/auth';
import type { Permission } from '../types/permissions';
import { AppLoader } from './ui';
import AppLayout from './layout/AppLayout';
import AccessDenied from './AccessDenied';

interface ProtectedRouteProps {
	children: React.ReactNode;
	allowedRoles?: UserRole[];
	/**
	 * Any-of permission gate. Unlike `allowedRoles` (which redirects a user who
	 * is on the wrong portal entirely), an unmet permission renders an explicit
	 * "Access Denied" so a direct URL can't quietly bounce the user elsewhere.
	 */
	requiredPermissions?: Permission[];
	/** Message shown when `requiredPermissions` is unmet. */
	accessDeniedMessage?: string;
}

const ProtectedRoute = ({
	children,
	allowedRoles,
	requiredPermissions,
	accessDeniedMessage,
}: ProtectedRouteProps) => {
	const { isAuthenticated, user, isInitialized } = useAuthStore();
	const { hasAny } = usePermissions();
	const { profile: providerProfile, profileError: providerProfileError } =
		useProviderProfileStore();
	const { profile: staffProfile, profileError: staffProfileError } = useStaffProfileStore();
	const location = useLocation();

	// Wait for auth to be initialized before making any redirect decisions
	if (!isInitialized) {
		return <AppLoader message="Loading" variant="fullscreen" />;
	}

	if (!isAuthenticated || !user) {
		return <Navigate to="/login" replace />;
	}

	// Check if user needs to change password (first-time login)
	const isStaff = user.role === USER_ROLE.STAFF;
	const changePasswordPath = isStaff ? '/staff/change-password' : '/providers/change-password';
	const isOnChangePasswordPage = location.pathname === changePasswordPath;

	if (user.passwordChangeRequired && !isOnChangePasswordPage) {
		return <Navigate to={changePasswordPath} replace />;
	}

	// For Salon Owner users, wait for provider profile to load before rendering.
	// Skip on the change-password page which doesn't need profile data.
	// Allow rendering if there's an error (pages can show their own error states).
	if (
		user.role === USER_ROLE.SALON_OWNER &&
		!isOnChangePasswordPage &&
		!providerProfile &&
		!providerProfileError
	) {
		return <AppLoader message="Loading" variant="fullscreen" />;
	}

	// For Staff users, wait for staff profile to load before rendering.
	if (isStaff && !isOnChangePasswordPage && !staffProfile && !staffProfileError) {
		return <AppLoader message="Loading" variant="fullscreen" />;
	}

	if (allowedRoles && !allowedRoles.includes(user.role)) {
		// Redirect based on role
		if (user.role === USER_ROLE.SALON_OWNER) {
			return <Navigate to="/providers/dashboard" replace />;
		}
		if (isStaff) {
			return <Navigate to="/staff/dashboard" replace />;
		}
		return <Navigate to="/dashboard" replace />;
	}

	// The profile carrying the permission list has loaded by this point (the
	// staff guard above blocks until it does), so an empty set is a real denial
	// rather than a loading flicker.
	if (requiredPermissions && requiredPermissions.length > 0 && !hasAny(...requiredPermissions)) {
		return (
			<AppLayout>
				<AccessDenied
					message={accessDeniedMessage}
					fallbackPath={isStaff ? '/staff/dashboard' : undefined}
				/>
			</AppLayout>
		);
	}

	return <>{children}</>;
};

export default ProtectedRoute;
